import { Skeleton } from "antd";
import moment from "moment";
import { memo, useContext, useState } from "react";
import { useHistory } from "react-router";

import { IconDotsThree } from "@btnet/blueprint-icons";
import { BTAvatar, btAvatarColorize } from "@btnet/core-components/BTAvatar";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTPopover } from "@btnet/core-components/BTPopover";
import { BTText } from "@btnet/core-components/BTText";
import type { EntityNotificationsModel } from "@btnet/services-btnet/handlers/models/entityNotificationsModel";
import type { NotificationModel } from "@btnet/services-btnet/handlers/models/notificationModel";
import { NotificationTypes } from "@btnet/services-btnet/handlers/models/notificationTypes";
import { cn } from "@btnet/utilities-core";

import {
    BdsIconEyeglasses,
    BdsIconEyeglassesSlash,
    BdsIconNotifications,
    BdsIconSlidersHorizontal,
} from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { useWebviewContext } from "helpers/webview/WebviewContext";

import { useTracking } from "utilities/analytics/analytics";
import { addQsParam, showAPIErrorMessage } from "utilities/apiHandler";
import { isRouteLocationState } from "utilities/flatRoutes/routes";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { DateDisplayRelative } from "commonComponents/utilities/DateDisplay/DateDisplayRelative";

import {
    getNotificationInfo,
    getNotificationTypeTestId,
    getPreferencesLink,
    type INotificationInfo,
} from "entity/notification/panel/notificationCard.utils";

import "./NotificationCard.less";

interface INotificationWithInfo {
    notification: NotificationModel;
    info: INotificationInfo;
}

interface INotificationCardProps {
    entityNotifications: EntityNotificationsModel;
    selectedJobId: number;
    onNotificationMarkedRead: (notification: NotificationModel, isRead: boolean) => Promise<void>;
    onNotificationClicked: (notification: NotificationModel) => Promise<void>;
    onNotificationUnfollowed: (notification: NotificationModel) => void;
    className?: string;
}

function isDiscussionAddedNotificationType(notificationType: NotificationTypes) {
    // client updates and dailylog and plans are being handled specifically
    const discussionsAddedArray: NotificationTypes[] = [
        NotificationTypes.SELECTION_DISCUSSION_ADDED,
        NotificationTypes.CHANGEORDER_DISCUSSION_ADDED,
        NotificationTypes.PO_DISCUSSION_ADDED,
        NotificationTypes.WARRANTY_DISCUSSION_ADDED,
        NotificationTypes.Bid_DiscussionAdded,
        NotificationTypes.Schedule_Item_Discussion_Added,
        NotificationTypes.TO_DO_DISCUSSION_ADDED,
        NotificationTypes.OwnerPaymentDiscussionAdded,
        NotificationTypes.PhotoCommentAdded,
        NotificationTypes.DocumentCommentAdded,
        NotificationTypes.VideoCommentAdded,
        NotificationTypes.RECEIPTS_DISCUSSION_ADDED,
        NotificationTypes.CREDITMEMO_DISCUSSION_ADDED,
        NotificationTypes.BILL_DISCUSSION_ADDED,
        NotificationTypes.UserMention,
        NotificationTypes.DepositDiscussionAdded,
        NotificationTypes.SELECTION_OPTION_DISCUSSION_ADDED,
    ];
    return discussionsAddedArray.includes(notificationType);
}

const addOpenCommentsParam = (
    notificationInfo: INotificationInfo,
    notification: NotificationModel,
) => {
    if (
        isDiscussionAddedNotificationType(notification.notificationType) &&
        typeof notificationInfo.link === "string" &&
        notificationInfo.link !== ""
    ) {
        const openCommentsParam = addQsParam(
            `commentId=${notification.notificationEntityId ?? 0}`,
            notificationInfo.link,
        );
        notificationInfo = {
            ...notificationInfo,
            link: notificationInfo.link + openCommentsParam,
        };
    }

    return notificationInfo;
};

export const NotificationCard = memo(function NotificationCard({
    entityNotifications,
    selectedJobId,
    onNotificationMarkedRead,
    onNotificationClicked,
    onNotificationUnfollowed,
    className,
}: INotificationCardProps) {
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const userInfo = useRequiredContext(UserInfoContext);
    const [isViewMoreOpen, setIsViewMoreOpen] = useState(false);
    const allRead = entityNotifications.notifications.every((x) => x.dateRead);
    const notificationsWithInfo = entityNotifications.notifications.map(
        (n) =>
            ({
                notification: n,
                info: addOpenCommentsParam(
                    getNotificationInfo(
                        n.entityActionType,
                        entityNotifications.entityId,
                        entityNotifications.jobId,
                        userInfo.loginType,
                        n.legacyEntityActionExtraData,
                        n.notificationType,
                        builderInfo,
                        n.notificationEntityId,
                        n.externalId,
                    ),
                    n,
                ),
            }) satisfies INotificationWithInfo,
    );

    const getViewMoreText = () => {
        if (isViewMoreOpen) {
            return "Show less";
        }

        const totalNotifications = entityNotifications.notifications.length;
        const additionalCount = totalNotifications - 1;
        const isMention =
            entityNotifications.notifications[0].notificationType === NotificationTypes.UserMention;

        if (isMention) {
            return `+${additionalCount} mention${additionalCount === 1 ? "" : "s"}`;
        }

        return "Show more";
    };

    if (notificationsWithInfo.length === 0) {
        throw new Error("NotificationCard rendered with no notifications");
    }

    const collapseFilteredNotifications = isViewMoreOpen
        ? notificationsWithInfo
        : notificationsWithInfo.slice(0, 1);

    const entityInfo = notificationsWithInfo[0].info;
    return (
        <BTRow
            className={cn("NotificationPanelCard flex-column", className)}
            responsiveMode="viewport"
        >
            <BTCol>
                <BTRow className="flex-column" responsiveMode="viewport">
                    {entityNotifications.jobName && (
                        <BTCol>
                            <BTRow align="middle" gutter={8} wrap={false} responsiveMode="viewport">
                                <BTCol className="text-overflow-auto">
                                    <BTText text={entityNotifications.jobName} />
                                </BTCol>
                            </BTRow>
                        </BTCol>
                    )}
                    <BTCol className="NotificationEntityTitle">
                        <BTRow gutter={4} align="middle" wrap={false} responsiveMode="viewport">
                            {!allRead && (
                                <div
                                    className="NotificationPanelPip EntityPip"
                                    data-testid="notificationEntityPip"
                                />
                            )}
                            {entityInfo.icon && <BTCol>{entityInfo.icon}</BTCol>}
                            <BTCol className="flex-column no-wrap">
                                <BTText size="normal-md" text={entityInfo.name} />
                            </BTCol>
                            {entityNotifications.entityTitle && (
                                <BTCol className="NotificationEntityName text-overflow-auto">
                                    <strong>
                                        <BTText
                                            size="normal-md"
                                            text={entityNotifications.entityTitle}
                                            className="text-overflow-auto"
                                            style={{ fontWeight: "unset", display: "inline" }}
                                        />
                                    </strong>
                                </BTCol>
                            )}
                        </BTRow>
                    </BTCol>
                </BTRow>
            </BTCol>
            <BTCol>
                <BTRow className="NotificationContent flex-column" responsiveMode="viewport">
                    {collapseFilteredNotifications.map((notificationWithInfo) => (
                        <BTCol
                            key={notificationWithInfo.notification.id}
                            className="NotificationItem"
                        >
                            <NotificationContentItem
                                notificationWithInfo={notificationWithInfo}
                                selectedJobId={selectedJobId}
                                onNotificationMarkedRead={onNotificationMarkedRead}
                                onNotificationClicked={onNotificationClicked}
                                onNotificationUnfollowed={onNotificationUnfollowed}
                            />
                        </BTCol>
                    ))}
                </BTRow>
            </BTCol>
            <BTCol>
                <BTRow justify="end" responsiveMode="viewport">
                    {entityNotifications.notifications.length > 1 && (
                        <BTCol>
                            {/* We can't use BdsButton here because of its custom styling, 
                                which shouldn't be done with Bds components */}
                            <BTButtonDeprecated
                                data-testid={
                                    isViewMoreOpen
                                        ? "notificationCardViewLess"
                                        : "notificationCardViewMore"
                                }
                                type="tertiary"
                                className="NotificationCardViewMore"
                                onClick={() => {
                                    setIsViewMoreOpen(!isViewMoreOpen);
                                }}
                            >
                                <BTText text={getViewMoreText()} />
                            </BTButtonDeprecated>
                        </BTCol>
                    )}
                </BTRow>
            </BTCol>
        </BTRow>
    );
});

interface INotificationContentItemProps {
    notificationWithInfo: INotificationWithInfo;
    selectedJobId: number;
    onNotificationMarkedRead: (notification: NotificationModel, markRead: boolean) => Promise<void>;
    onNotificationClicked: (notification: NotificationModel) => Promise<void>;
    onNotificationUnfollowed: (notification: NotificationModel) => void;
}

function NotificationContentItem({
    notificationWithInfo,
    selectedJobId,
    onNotificationMarkedRead,
    onNotificationClicked,
    onNotificationUnfollowed,
}: INotificationContentItemProps) {
    const notification = notificationWithInfo.notification;
    const info = notificationWithInfo.info;
    const history = useHistory();
    const { trackEvent } = useTracking();
    const spaInfoContext = useContext(SpaInfoContext);
    const isWebview = useWebviewContext() !== undefined;

    const handleNotificationClick = async () => {
        trackEvent({
            event: "MenuItemClick",
            testid: "notificationPanelNotificationClick",
            uniqueId: "notificationPanelNotificationClick",
            extraInfo: {
                notificationId: notification.id,
                notificationType: getNotificationTypeTestId(notification.notificationType),
                dateSent: notification.dateSent.toISOString(),
                dateRead: notification.dateRead?.toISOString(),
            },
        });

        if (!info.link) {
            await onNotificationMarkedRead(notification, true);
            return;
        }

        // Close the panel once we know there is a link, because we are going to navigate away
        try {
            await onNotificationClicked(notification);
        } catch (error) {
            // Catch the error and continue so we can open the link to the notification
            showAPIErrorMessage(error);
        }

        if (info.isExternalLink && typeof info.link === "string") {
            const externalUrl = info.link.startsWith("/") ? info.link : `/${info.link}`;
            window.open(`/app${externalUrl}`, "_blank", "noopener,noreferrer");
            return;
        }

        // Find link
        let nextUrl = "";
        if (isRouteLocationState(info.link)) {
            nextUrl = info.link.pathname;
        } else {
            nextUrl = info.link;
        }

        // Clean link up
        let url = nextUrl;
        if (!url.startsWith("/")) {
            url = "/" + url;
        }
        if (url.startsWith("/app")) {
            url = url.replace("/app", "");
        }
        if (!info.isFullPageRedirect) {
            url = `${history.location.pathname}/NotificationPanel${url}`;
        }

        // Got to link
        if (url.includes(".aspx")) {
            window.location.assign(url);
        }
        // Check for if we are routing to full pages from non SPA
        else if (spaInfoContext?.isSpa || !info.isFullPageRedirect) {
            history.push(url);
        } else {
            window.location.assign(`/app${url}`);
        }
    };

    return (
        <div onClick={handleNotificationClick}>
            {!notification.dateRead && (
                <div
                    className="NotificationPanelPip ContentPip"
                    data-testid="notificationContentPip"
                />
            )}
            <BTRow
                className="NotificationContentItem flex-column"
                style={{ rowGap: "6px" }}
                wrap={false}
                responsiveMode="viewport"
                data-testid={`notificationContentItem-${getNotificationTypeTestId(
                    notification.notificationType,
                )}`}
            >
                <BTCol className="NotificationContentItemHeader">
                    <BTRow
                        justify="space-between"
                        align="top"
                        wrap={false}
                        gutter={4}
                        responsiveMode="viewport"
                    >
                        <BTCol className="NotificationContentItemTitle">
                            <BTRow align="top" wrap={false} gutter={6} responsiveMode="viewport">
                                {!!notification.performingOrgLinkId && (
                                    <BTCol className="NotificationContentItemAvatar">
                                        <BTAvatar
                                            testid={`notificationAvatar-${notification.performingOrgLinkId}`}
                                            size="xs"
                                            text={notification.performingUserName ?? ""}
                                            colorize={
                                                btAvatarColorize[
                                                    notification.performingOrgLinkId %
                                                        btAvatarColorize.length
                                                ]
                                            }
                                        />
                                    </BTCol>
                                )}

                                <BTCol className="NotificationContentItemTitleText">
                                    <BTText size="normal-sm" text={notification.title} />
                                </BTCol>
                            </BTRow>
                        </BTCol>
                        <BTCol className="NotificationContentItemDate">
                            <DateDisplayRelative
                                data-chromatic="ignored"
                                value={moment(notification.dateSent)}
                            />
                        </BTCol>
                        {!isWebview && (
                            <BTCol>
                                <NotificationMenu
                                    notification={notification}
                                    selectedJobId={selectedJobId}
                                    onNotificationMarkedRead={async (isRead) =>
                                        await onNotificationMarkedRead(notification, isRead)
                                    }
                                    onNotificationUnfollowed={() =>
                                        onNotificationUnfollowed(notification)
                                    }
                                />
                            </BTCol>
                        )}
                    </BTRow>
                </BTCol>

                {notification.body && (
                    <BTCol className="NotificationContentItemBody">
                        <BTText
                            size="normal-sm"
                            text={notification.body}
                            className="NotificationContentItemBodyText"
                        />
                    </BTCol>
                )}
            </BTRow>
        </div>
    );
}

interface INotificationMenuProps {
    notification: NotificationModel;
    selectedJobId: number;
    onNotificationMarkedRead: (isRead: boolean) => Promise<void>;
    onNotificationUnfollowed: () => void;
}

function NotificationMenu({
    notification,
    selectedJobId,
    onNotificationMarkedRead,
    onNotificationUnfollowed,
}: INotificationMenuProps) {
    const userInfo = useRequiredContext(UserInfoContext);
    const [isOpen, setIsOpen] = useState(false);
    return (
        <BTPopover
            trigger="click"
            placement="bottomLeft"
            widthType="unrestricted"
            borderless
            visible={isOpen}
            onVisibleChange={setIsOpen}
            stopPropagation
            overlayClassName="NotificationMenuOverlay"
            // The panel renders as an antd dropdown overlay, which BTPopover's default
            // getPopupContainer doesn't recognize — it would portal to <body> and clamp the
            // menu to the viewport instead of the panel. Mount into the panel so the menu
            // stays inside it and scrolls with it, as the previous BdsFloat root/boundary did.
            getPopupContainer={(trigger) =>
                trigger.closest<HTMLElement>(".NotificationPanel") ?? document.body
            }
            content={
                <div
                    data-testid={`notificationMenu-${getNotificationTypeTestId(
                        notification.notificationType,
                    )}`}
                    aria-label="Notification Menu"
                    className="NotificationMenuContent"
                >
                    <BTMenu
                        testid="notificationContextMenu"
                        className="NotificationContextMenu"
                        selectable={false}
                        style={{ margin: 0 }}
                    >
                        {notification.dateRead && (
                            <BTMenu.Item
                                testid={`markNotificationUnread-${getNotificationTypeTestId(
                                    notification.notificationType,
                                )}`}
                                className="NotificationMenuDropdownItem"
                                style={{ margin: 0 }}
                                onClick={async ({ domEvent }) => {
                                    domEvent.stopPropagation();
                                    // Void so that we close the menu right away as we don't have loading states
                                    void onNotificationMarkedRead(false);
                                    setIsOpen(false);
                                }}
                            >
                                <div className="flex flex-row NotificationMenuActionRow">
                                    <BdsIconEyeglassesSlash />
                                    <BTText
                                        text="Mark as unread"
                                        size="normal-md"
                                        className="NotificationMenuText"
                                    />
                                </div>
                            </BTMenu.Item>
                        )}
                        {!notification.dateRead && (
                            <BTMenu.Item
                                testid={`markNotificationRead-${getNotificationTypeTestId(
                                    notification.notificationType,
                                )}`}
                                className="NotificationMenuDropdownItem"
                                style={{ margin: 0 }}
                                onClick={async ({ domEvent }) => {
                                    domEvent.stopPropagation();
                                    // Void so that we close the menu right away as we don't have loading states
                                    void onNotificationMarkedRead(true);
                                    setIsOpen(false);
                                }}
                            >
                                <div className="flex flex-row NotificationMenuActionRow">
                                    <BdsIconEyeglasses />
                                    <BTText
                                        text="Mark as read"
                                        size="normal-md"
                                        className="NotificationMenuText"
                                    />
                                </div>
                            </BTMenu.Item>
                        )}
                        {notification.canUnfollow && (
                            <BTMenu.Item
                                testid={`markNotificationUnfollowed-${getNotificationTypeTestId(
                                    notification.notificationType,
                                )}`}
                                className="NotificationMenuDropdownItem"
                                style={{ margin: 0 }}
                                onClick={({ domEvent }) => {
                                    domEvent.stopPropagation();
                                    onNotificationUnfollowed();
                                    setIsOpen(false);
                                }}
                            >
                                <div className="flex flex-row NotificationMenuActionRow">
                                    <BdsIconNotifications />
                                    <BTText
                                        text="Unfollow..."
                                        size="normal-md"
                                        className="NotificationMenuText"
                                    />
                                </div>
                            </BTMenu.Item>
                        )}
                        <BTMenu.Item
                            testid="notificationPreferencesMenuItem"
                            className="NotificationMenuDropdownItem"
                            style={{ margin: 0 }}
                        >
                            <BTLinkRelative
                                className="NotificationLink"
                                to={`/NotificationPanel${getPreferencesLink(
                                    userInfo.globalUserId!,
                                    selectedJobId,
                                )}`}
                                isUnderline={false}
                                onClick={async (event) => {
                                    event.stopPropagation();
                                    setIsOpen(false);
                                }}
                                data-testid={`openNotificationPreferences-${getNotificationTypeTestId(
                                    notification.notificationType,
                                )}`}
                            >
                                <div className="flex flex-row NotificationMenuActionRow">
                                    <BdsIconSlidersHorizontal />
                                    <BTText
                                        text="Preferences"
                                        size="normal-md"
                                        className="NotificationMenuText"
                                    />
                                </div>
                            </BTLinkRelative>
                        </BTMenu.Item>
                    </BTMenu>
                </div>
            }
        >
            <BTButton
                data-testid={`openNotificationMenu-${getNotificationTypeTestId(
                    notification.notificationType,
                )}`}
                type="tertiary"
                aria-label="Notifications menu"
                icon={<IconDotsThree />}
            />
        </BTPopover>
    );
}

function NotificationContentSkeleton() {
    return (
        <BTRow className="NotificationContentItem flex-column" wrap={false}>
            <BTCol className="NotificationContentItemHeader">
                <BTRow
                    justify="space-between"
                    align="top"
                    wrap={false}
                    gutter={4}
                    responsiveMode="viewport"
                >
                    <BTCol flex="auto" className="NotificationContentItemTitle">
                        <div
                            className="flex margin-bottom-xs align-items-center"
                            style={{ gap: "8px" }}
                        >
                            <Skeleton.Avatar active size="small" />
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 1, width: "100%", className: "margin-all-zero" }}
                            />
                        </div>
                    </BTCol>
                </BTRow>
            </BTCol>
            <BTCol className="NotificationContentItemBody">
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 1, width: "80%", className: "margin-all-zero" }}
                />
            </BTCol>
        </BTRow>
    );
}

export function NotificationCardSkeleton({ className }: { className?: string }) {
    return (
        <div
            className={cn("flex NotificationPanelCard flex-column", className)}
            style={{ gap: "8px" }}
        >
            <div className="flex flex-column" style={{ gap: "8px" }}>
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 1, width: "40%", className: "margin-all-zero" }}
                />
                <Skeleton
                    active
                    title={false}
                    paragraph={{ rows: 1, width: "80%", className: "margin-all-zero" }}
                />
            </div>
            <BTRow
                className="NotificationContent flex-column"
                style={{ rowGap: "4px" }}
                responsiveMode="viewport"
            >
                <BTCol>
                    <NotificationContentSkeleton />
                </BTCol>
            </BTRow>
        </div>
    );
}
